import { ContentMetadata } from "generated/CoreApi_v1";
import HtmlUtils from "lib/HtmlUtils";
import LibUtils from "lib/LibUtils";
import { Accept } from "react-dropzone";
import { SessionService } from "services/sessionService";

export default class MediaUtils {

    /**
     * Retorna o objeto de aceitação do Dropzone para imagens.
     * @returns
     */
    /**
     * Retorna o objeto de aceitação do Dropzone para imagens.
     * @returns Objeto Accept para imagens
     */
    static DropzoneAcceptImages(): Accept {
        return {
            "images/*": this.AcceptImages()
        };
    }

    /**
     * Retorna o objeto de aceitação do Dropzone para vídeos.
     * @returns Objeto Accept para vídeos
     */
    static DropzoneAcceptVideos(): Accept {
        return {
            "video/*": this.AcceptVideos()
        };
    }

    /**
     * Retorna a lista de extensões aceitas para imagens.
     * @returns Array de extensões de imagem
     */
    static AcceptImages(): string[] {
        return [
            ".png",
            ".jpg",
            ".jpeg",
            ".gif"
        ];
    }

    /**
     * Retorna a lista de extensões aceitas para vídeos.
     * @returns Array de extensões de vídeo
     */
    static AcceptVideos(): string[] {
        return [
            ".3gp",
            ".flv",
            ".wmv",
            ".asf",
            ".avi",
            ".mov",
            ".mkv",
            ".mp4"
        ];
    }

    /**
     * Retorna a lista de extensões aceitas para áudios.
     * @returns Array de extensões de áudio
     */
    static AcceptAudios(): string[] {
        return [
            ".mp3",
            ",m4a"
        ];
    }

    /**
     * Retorna a lista de extensões de vídeo suportadas apenas pelo navegador.
     * @returns Array de extensões de vídeo compatíveis com navegador
     */
    static AcceptBrowserVideosOnly(): string[] {
        return [
            ".mp4",
            ".webm",
            ".ogg"
        ];
    }

    /**
     * Retorna a lista de extensões aceitas para documentos.
     * @returns Array de extensões de documentos
     */
    static AcceptDocuments(): string[] {
        return [
            ".pdf",
            ".doc",
            ".docx",
            ".xls",
            ".xlsx",
            ".ppt",
            ".pptx",
            ".zip",
            ".txt",
            ".csv"
        ];
    }

    /**
     * Retorna a URL do ícone para um determinado nome de arquivo com base em sua extensão.
     * @param fileName Nome do arquivo
     * @returns URL do ícone correspondente
     */
    static getFileIcon(fileName: string): string {
        let src = "/img/contenttype/OtherDocument.png";
        if (fileName) {
            if (fileName.indexOf(".pdf") > -1)
                src = "/img/contenttype/Pdf.png";
            else if (fileName.indexOf(".doc") > -1)
                src = "/img/contenttype/Word.png";
            else if (fileName.indexOf(".xls") > -1)
                src = "/img/contenttype/Excel.png";
            else if (fileName.indexOf(".ppt") > -1)
                src = "/img/contenttype/PowerPoint.png";
            else if (fileName.indexOf(".txt") > -1)
                src = "/img/contenttype/Text.png";
            else if (fileName.indexOf(".zip") > -1)
                src = "/img/contenttype/Zip.png";
        }
        return src;
    }

    /**
     * Verifica se o mimetype corresponde a um arquivo de texto ou CSV.
     * @param mimetype Tipo MIME do arquivo
     * @returns Verdadeiro se for arquivo de texto ou CSV
     */
    static isTextOrCsv(mimetype: string): boolean {
        return mimetype.startsWith("text");
    }

    /**
     * Verifica se o nome do arquivo corresponde a um documento PDF.
     * @param filename Nome do arquivo
     * @returns Verdadeiro se for PDF
     */
    static isPDF(filename: string): boolean {
        return filename.indexOf(".pdf") > -1;
    }

    /**
     * Verifica se o nome do arquivo corresponde a um documento do Word.
     * @param filename Nome do arquivo
     * @returns Verdadeiro se for documento do Word
     */
    static isDocument(filename: string): boolean {
        return filename.indexOf(".doc") > -1;
    }

    static isSheet(filename: string): boolean {
        return filename.indexOf(".xls") > -1;
    }

    /**
     * Verifica se o nome do arquivo corresponde a uma apresentação (PowerPoint).
     * @param filename Nome do arquivo
     * @returns Verdadeiro se for apresentação
     */
    static isSlides(filename: string): boolean {
        return filename.indexOf(".ppt") > -1;
    }

    /**
     * Verifica se o nome do arquivo corresponde a um PDF.
     * @param filename Nome do arquivo
     * @returns Verdadeiro se for PDF
     */
    static isPdf(filename: string): boolean {
        return filename.indexOf(".pdf") > -1;
    }

    /**
     * Verifica se o nome do arquivo corresponde a um documento do Office (Word, Excel ou PowerPoint).
     * @param filename Nome do arquivo
     * @returns Verdadeiro se for documento do Office
     */
    static isOfficeDocument(filename: string): boolean {
        return MediaUtils.isDocument(filename) || MediaUtils.isSheet(filename) || MediaUtils.isSlides(filename);
    }

    /**
     * Verifica se o mimetype corresponde a uma imagem.
     * @param mimetype Tipo MIME
     * @returns Verdadeiro se for imagem
     */
    static isImage(mimetype: string): boolean {
        return mimetype.startsWith("image");
    }

    /**
     * Verifica se o mimetype corresponde a áudio ou vídeo.
     * @param mimetype Tipo MIME
     * @returns Verdadeiro se for áudio ou vídeo
     */
    static isVideoOrAudio(mimetype: string): boolean {
        return mimetype.startsWith("audio") || mimetype.startsWith("video");
    }

    /**
     * Verifica se o mimetype corresponde a vídeo.
     * @param mimetype Tipo MIME
     * @returns Verdadeiro se for vídeo
     */
    static isVideo(mimetype: string): boolean {
        return mimetype.startsWith("video");
    }

    /**
     * Verifica se o mimetype corresponde a áudio.
     * @param mimetype Tipo MIME
     * @returns Verdadeiro se for áudio
     */
    static isAudio(mimetype: string): boolean {
        return mimetype.startsWith("audio");
    }

    /**
     * Gera a URL para visualização de documentos do Office online.
     * @param url URL do arquivo
     * @returns URL para visualização no Office Online
     */
    static async makeOfficeViewerUrl(url: string) {
        if (HtmlUtils.isLocalhost()) return url;

        const token = await SessionService.getAccessToken();
        if (token != "") {
            url += url.includes("?") ? "&" : "?";
            url += "ptn=" + document.location.hostname;
            url += "&access_token=" + await SessionService.getAccessToken();
            return `https://view.officeapps.live.com/op/view.aspx?src=${url}`;
        }
        return "";
    }

    /**
     * Abre uma URL em uma nova janela, utilizando visualizador do Office se necessário.
     * @param filename Nome do arquivo
     * @param url URL do arquivo
     * @param windowName Nome da janela
     * @returns Resultado da abertura ou alerta
     */
    static async openUrlInNewWindow(filename: string, url: string, windowName: string) {
        if (this.isOfficeDocument(filename))
            url = await this.makeOfficeViewerUrl(url);

        if (url != "")
            return HtmlUtils.openUrlInNewWindow(url, windowName);

        alert('Não foi possível obter acesso ao arquivo para visualização. Tente novamente.');
        return undefined;
    }

    /**
     * Abre o visualizador de arquivos ou faz download, dependendo do tipo do arquivo.
     * @param filename Nome do arquivo
     * @param url URL do arquivo
     */
    static async openFileViewerOrDownloadUrl(filename: string, url: string) {
        if (this.isOfficeDocument(filename))
            HtmlUtils.openUrlInNewWindow(await this.makeOfficeViewerUrl(url));
        else if (this.isPdf(filename))
            HtmlUtils.openUrlInNewWindow(url);
        else
            HtmlUtils.downloadUrl(url, filename);
    }

    /**
     * Monta a URL de embed do YouTube para um vídeo.
     * @param externalId ID externo do vídeo
     * @returns URL de embed do YouTube
     */
    static buildYoutubeEmbedUrl(externalId: string): string {
        return `https://www.youtube.com/embed/${externalId}`;
    }

    /**
     * Monta o HTML de embed do YouTube para um vídeo.
     * @param meta Metadados do conteúdo
     * @param autoPlay Se deve iniciar automaticamente
     * @returns HTML de embed do YouTube
     */
    static buildYoutubeEmbedHtml(meta: ContentMetadata, autoPlay: boolean): string {
        return `<iframe
                    src="${this.buildYoutubeEmbedUrl(meta.externalId!)}${autoPlay ? "?autoplay=1&muted=1" : ""}"
                    allow="${autoPlay ? "autoplay" : ""}""
                    width="${meta.width}"
                    height="${meta.height}"
                >
                </iframe>`;
    }

    /**
     * Valida se o arquivo é aceito pelo input, de acordo com as extensões.
     * @param filename Nome do arquivo
     * @param accept Extensões aceitas
     * @returns Verdadeiro se aceito
     */
    static validFileInput(filename: string, accept: string | string[]) {
        if (accept == null) return true;

        const compare = typeof accept == "string" ? accept.toLowerCase().split(",") : accept;
        if (compare.length == 0) return true;

        const extension = "." + LibUtils.getFileExtension(filename);
        return compare.findIndex(x => x.startsWith("application") || x == extension) > -1;
    }

    /**
     * Lê o conteúdo de um arquivo como string.
     * @param file Arquivo a ser lido
     * @returns Promise com o conteúdo do arquivo em string
     */
    static readFileAsString(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            var reader = new FileReader();
            reader.onload = (event: any) => {
                resolve(event.target.result);
            };
            reader.onerror = (e: any) => {
                reject(e);
            };
            reader.readAsText(file);
        });
    }
}
